/**
 * Common utility functions for booking payment calculations
 */

import { PAYMENT_OPTIONS } from "pagesV2/pro/Bookings/constants"

// Rounds a monetary value to cents, eliminating floating-point drift
// (e.g. 1100 - 0 surfacing as 1099.9999999). Without this, validators that
// compare against the balance can reject the true outstanding amount.
export const roundToCents = (value) => Math.round((Number(value) || 0) * 100) / 100

/**
 * Whether the remaining balance will be collected in CASH (so only the deposit is paid by card).
 */
export const isBalanceInCash = (paymentOption) =>
  [PAYMENT_OPTIONS.deposit_cash, PAYMENT_OPTIONS.deposit_link_in_cash].includes(paymentOption)

/**
 * The fraction of the pre-card total that the card processing fee is charged on (passed to
 * calculateBookingTotal as options.depositFraction).
 * - Full payment, or deposit-now + balance-by-card: the WHOLE total is (or will be) paid by card,
 *   so the full card fee is taken up front → fraction 1.
 * - Deposit-now + balance-in-cash: only the deposit is paid by card, so the fee applies to the
 *   deposit only → fraction = deposit% / 100.
 */
export const getCardFeeFraction = ({ depositOnly, depositPercent, customDepositAmount, balanceInCash = false } = {}) => {
  if (!depositOnly || !balanceInCash) return 1
  const pct = Number(customDepositAmount || depositPercent) || 0
  return pct > 0 && pct < 100 ? pct / 100 : 1
}

/**
 * Card processing fee grossed up on `base` so that after Stripe's deduction on (base + fee) the
 * net equals `base`. Returns 0 when passthrough is off, there's no rate, or base <= 0.
 */
export const grossUpCardFee = (base, { rate = 0, fixed = 0.3, enabled = true } = {}) => {
  const r = Number(rate) || 0
  if (!enabled || r <= 0 || r >= 1 || base <= 0) return 0
  return (base * r + (Number(fixed) || 0)) / (1 - r)
}

/**
 * Resolves the card fee config from an experience's settings. `enabled` is true only when the
 * guide passes the fee to the guest (card_fee_passthrough_enabled) and an active "Card Processing
 * Fee" entry exists. `rate` is that entry's percent / 100; `fixed` defaults to Stripe's $0.30.
 */
export const resolveCardFeeConfig = ({ customFees = [], platformFeeSetting } = {}) => {
  const entry = (customFees || []).find((f) => f?.title === "Card Processing Fee" && f?.is_active !== false)
  const enabled = platformFeeSetting?.card_fee_passthrough_enabled === true && !!entry
  return { rate: entry ? Number(entry.price) / 100 : 0, fixed: 0.3, enabled }
}

/**
 * Whether the (possibly newly-selected, in edit mode) experience requires a deposit at booking.
 *
 * In edit mode the guide can swap the booking to a different experience, but the booking's own
 * `payment_option` only reflects whichever experience it was ORIGINALLY booked against — nothing
 * re-derives it from a mid-edit experience switch (the deposit/full-payment picker UI is hidden
 * entirely while editing). So when the experience was ACTUALLY swapped during this edit, the
 * newly-selected experience's own `deposit_required` flag is authoritative. Otherwise — no swap,
 * even though `experienceDetail` (the original experience's own detail, fetched for display) is
 * loaded — the booking's own `payment_option` is still the source of truth: it reflects the
 * guide's deliberate original "Collect deposit only" choice, which can legitimately differ from
 * what its experience currently supports (e.g. a full-payment booking against a deposit-capable
 * experience must stay full-payment through unrelated price-affecting edits).
 */
export const resolveHasDepositPayment = ({ booking, paymentOption, experienceDetail, isEdit, isExperienceChanged } = {}) => {
  if (isEdit && experienceDetail?.id && isExperienceChanged) return !!experienceDetail?.deposit_required

  const { deposit_later, deposit_cash, deposit_link, deposit_link_in_cash } = PAYMENT_OPTIONS
  return [deposit_later, deposit_cash, deposit_link, deposit_link_in_cash].includes(booking?.payment_option || paymentOption)
}

export const calculatePaymentBreakdown = ({
  booking = {},
  bookingSelections = {},
  depositPercent = 0,
  customDepositAmount = 0,
  paymentOption = "",
  isDepositOverride
}) => {
  const { payment_summary = {}, payment_history = [], payment_option } = booking
  const { deposit_later, deposit_cash, deposit_link, deposit_link_in_cash } = PAYMENT_OPTIONS

  const { remaining_balance = 0 } = payment_summary
  const payment_status = booking?.payment_status

  // bookingSelections.amount is already net of the discount (applied before tax in
  // calculateBookingTotal), so it must NOT be discounted again here. It is the grand total and
  // includes the card processing fee on the deposit (see calculateBookingTotal's depositFraction).
  const amount = Math.max(0, bookingSelections?.amount || 0)
  // preCardTotal is the total before any card fee — the base the deposit % is taken from.
  // cardProcessingFee is the deposit's card fee, charged on top of the deposit (paid now).
  const preCardTotal = Math.max(0, bookingSelections?.preCardTotal ?? amount)
  const cardProcessingFee = Math.max(0, bookingSelections?.cardProcessingFee || 0)
  const percent = Number(customDepositAmount || depositPercent) || 0
  // isDepositOverride (see resolveHasDepositPayment) takes precedence in edit mode, where
  // booking.payment_option can be stale relative to a mid-edit experience switch.
  const isDeposit =
    isDepositOverride != null
      ? isDepositOverride
      : [deposit_later, deposit_cash, deposit_link, deposit_link_in_cash].includes(payment_option || paymentOption)

  // Deposit calculations
  // Prefer the backend's authoritative payment_summary.deposit_paid_so_far (see
  // TripBooking#deposit_paid_so_far) over reconstructing it from payment_history row labels -
  // the two used to diverge (backend treated ALL confirmed income as "paid toward deposit" while
  // this filtered only rows labeled "deposit"), which is exactly what let a booking with 2+ prior
  // incomes skip real deposit collection on edit while this preview showed the correct-looking
  // number. Falls back to the payment_history reconstruction when the field isn't present (e.g.
  // the new-booking creation flow, which has no persisted payment_summary yet).
  const paidDepositAmount = Number.isFinite(payment_summary?.deposit_paid_so_far)
    ? payment_summary.deposit_paid_so_far
    : payment_history
        ?.filter((payment) => payment?.type?.toLowerCase() === "deposit")
        .reduce((sum, payment) => sum + (payment?.amount || 0), 0) || 0

  // The deposit % applies to the pre-card total; the card fee (charged only on the amount paid by
  // card now) is then added on top. So the amount due now = deposit base + its card fee.
  const depositBase = (preCardTotal * percent) / 100
  const depositAmount = depositBase + cardProcessingFee
  const depositDueNow = Math.max(0, depositAmount - paidDepositAmount)

  // Balance calculations
  // Exclude tips from total paid amount as they don't reduce the booking balance
  const totalPaidAmount =
    payment_history?.reduce((sum, payment) => {
      if (payment?.type?.toLowerCase() === "tip") return sum
      return sum + (payment?.amount || 0)
    }, 0) || 0
  const refundDueNow = totalPaidAmount > amount ? roundToCents(Math.abs(totalPaidAmount - amount)) : 0
  // The card processing fee is charged up front with the deposit (full fee when the balance is by
  // card, deposit-only fee when the balance is in cash), so the remaining balance carries no card
  // fee. amount already includes that fee and depositDueNow already accounts for it.
  const balanceAmount = roundToCents(isDeposit ? amount - totalPaidAmount - depositDueNow : amount - totalPaidAmount)

  return {
    // Core values
    amount,
    percent,
    remaining_balance,

    // Deposit related
    depositAmount,
    depositDueNow,
    paidDepositAmount,

    // Payment related
    balanceAmount,

    // Status flags
    payment_status,
    isDeposit,

    // Raw data
    payment_history,
    payment_summary,
    refundDueNow
  }
}
